/**
 * Property pickers for issues (and the people/team pickers used elsewhere).
 * Each is controlled: pass `value` and `onChange`. Pass `children` for a
 * custom trigger, or use the default chip.
 */
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { currentCycle, useCycles, useLabelsFor, useStates, useTeams, useUsers } from "@/lib/hooks";
import { ESTIMATES, PRIORITIES, dueLabel, estimateLabel, shortDate } from "@/lib/format";
import { useRow, useRows, useTable } from "@/lib/store";
import type { Id } from "@/lib/types";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { Box, CalendarDays, CircleDashed, Diamond, Hexagon, RefreshCcw, Tag, Triangle, UserRound, Users } from "lucide-react";
import { forwardRef, type ComponentProps, type ReactNode } from "react";
import { UserAvatar } from "./avatar";
import { ColorDot, PriorityIcon, ProjectStatusIcon, StateIcon, StateIconFor, TeamIcon } from "./icons";
import { Picker, type PickerOption } from "./picker";

/** Small bordered button used as the default trigger. */
export const Chip = forwardRef<HTMLButtonElement, ComponentProps<"button"> & { muted?: boolean }>(function Chip({ className, muted, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn(
        "inline-flex h-7 max-w-full items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[12.5px] pointer-coarse:h-9 pointer-coarse:px-3 pointer-coarse:text-[14px] whitespace-nowrap shadow-[0_1px_1px_rgb(0_0_0/0.03)] transition-colors hover:bg-accent dark:bg-transparent focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-60",
        muted && "text-muted-foreground",
        className,
      )}
    >
      {children}
    </button>
  );
});

/** Borderless trigger for property rows in sidebars. */
export const Ghost = forwardRef<HTMLButtonElement, ComponentProps<"button">>(function Ghost({ className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={cn("-mx-1.5 inline-flex h-7 min-w-0 items-center gap-2 rounded-md px-1.5 text-[13px] pointer-coarse:h-10 pointer-coarse:text-[15px] transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none", className)}
    >
      {children}
    </button>
  );
});

interface Controlled<V> {
  value: V;
  onChange: (v: V) => void;
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  align?: "start" | "center" | "end";
}

export function StatusPicker({ teamId, ...p }: Controlled<Id> & { teamId: Id }) {
  const states = useStates(teamId);
  const current = useRow("workflowState", p.value);
  const options: PickerOption<Id>[] = states.map((s) => ({ value: s.id, label: s.name, icon: <StateIcon category={s.category} color={s.color} /> }));
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder="Change status…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip>
          <StateIconFor stateId={p.value} />
          {current?.name ?? "Status"}
        </Chip>
      )}
    </Picker>
  );
}

export function PriorityPicker(p: Controlled<number>) {
  const options = [1, 2, 3, 4, 0].map((v) => ({ value: v, label: PRIORITIES.find((x) => x.value === v)!.label, icon: <PriorityIcon priority={v} /> }));
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder="Set priority…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={p.value === 0}>
          <PriorityIcon priority={p.value} />
          {PRIORITIES.find((x) => x.value === p.value)?.label}
        </Chip>
      )}
    </Picker>
  );
}

export function AssigneePicker({ label = "Assignee", ...p }: Controlled<Id | null> & { label?: string }) {
  const users = useUsers();
  const me = useRow("user", p.value);
  const options: PickerOption<Id | null>[] = [
    { value: null, label: label === "Assignee" ? "No assignee" : `No ${label.toLowerCase()}`, icon: <UserAvatar userId={null} size="sm" /> },
    ...users.map((u) => ({ value: u.id, label: u.name, icon: <UserAvatar userId={u.id} size="sm" />, keywords: [u.email] })),
  ];
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder={label === "Assignee" ? "Assign to…" : `Set ${label.toLowerCase()}…`} open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={!p.value}>
          <UserAvatar userId={p.value} size="xs" />
          {me?.name ?? label}
        </Chip>
      )}
    </Picker>
  );
}

export function PeoplePicker({ label = "People", ...p }: Controlled<Id[]> & { label?: string }) {
  const users = useUsers();
  const options = users.map((u) => ({ value: u.id, label: u.name, icon: <UserAvatar userId={u.id} size="sm" />, keywords: [u.email] }));
  return (
    <Picker multiple options={options} value={p.value} onChange={p.onChange} placeholder="Add people…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={p.value.length === 0}>
          <Users className="size-3.5" />
          {p.value.length ? `${p.value.length} ${label.toLowerCase()}` : label}
        </Chip>
      )}
    </Picker>
  );
}

export function LabelsPicker({ teamId, scope = "issue", ...p }: Controlled<Id[]> & { teamId: Id | null; scope?: "issue" | "project" | "idea" }) {
  const labels = useLabelsFor(teamId, scope);
  const all = useTable("label");
  const options: PickerOption<Id>[] = labels
    .filter((l) => !l.isGroup)
    .map((l) => ({ value: l.id, label: l.name, icon: <ColorDot color={l.color} />, group: l.parentId ? all[l.parentId]?.name : undefined }));
  const onChange = (next: Id[]) => {
    // Keep one label per group: the newest pick wins.
    const added = next.find((x) => !p.value.includes(x));
    const group = added ? all[added]?.parentId : null;
    p.onChange(group ? next.filter((x) => x === added || all[x]?.parentId !== group) : next);
  };
  return (
    <Picker multiple options={options} value={p.value} onChange={onChange} placeholder="Add labels…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={p.value.length === 0}>
          <Tag className="size-3.5" />
          {p.value.length ? p.value.map((id) => all[id]?.name).filter(Boolean).join(", ") : "Labels"}
        </Chip>
      )}
    </Picker>
  );
}

export function ProjectPicker(p: Controlled<Id | null> & { teamId?: Id | null }) {
  const projects = useRows("project", (x) => x.status !== "completed" && x.status !== "canceled", (a, b) => a.name.localeCompare(b.name));
  const current = useRow("project", p.value);
  const options: PickerOption<Id | null>[] = [
    { value: null, label: "No project", icon: <Box className="size-4 text-muted-foreground" /> },
    ...projects.map((x) => ({ value: x.id, label: x.name, icon: <ProjectStatusIcon status={x.status} />, group: p.teamId && x.teamIds.includes(p.teamId) ? "This team" : "Other projects" })),
  ];
  options.sort((a, b) => (a.group === b.group ? 0 : a.group === "This team" ? -1 : b.group === "This team" ? 1 : 0));
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder="Move to project…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={!p.value}>
          <Hexagon className="size-3.5" style={current ? { color: current.color } : undefined} />
          <span className="truncate">{current?.name ?? "Project"}</span>
        </Chip>
      )}
    </Picker>
  );
}

export function MilestonePicker({ projectId, ...p }: Controlled<Id | null> & { projectId: Id | null }) {
  const milestones = useRows("projectMilestone", (m) => m.projectId === projectId, (a, b) => (a.sortKey < b.sortKey ? -1 : 1), [projectId]);
  const current = useRow("projectMilestone", p.value);
  if (!projectId) return null;
  const options: PickerOption<Id | null>[] = [{ value: null, label: "No milestone", icon: <Diamond className="size-4 text-muted-foreground" /> }, ...milestones.map((m) => ({ value: m.id, label: m.name, icon: <Diamond className="size-4" />, hint: m.targetDate ? shortDate(m.targetDate) : undefined }))];
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder="Set milestone…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={!p.value}>
          <Diamond className="size-3.5" />
          {current?.name ?? "Milestone"}
        </Chip>
      )}
    </Picker>
  );
}

export function cycleName(c: { number: number | null; name: string | null }) {
  // A cycle created on this device has no number until the server assigns one.
  return c.name || (c.number != null ? `Cycle ${c.number}` : "New cycle");
}

export function CyclePicker({ teamId, ...p }: Controlled<Id | null> & { teamId: Id }) {
  const team = useRow("team", teamId);
  const cycles = useCycles(teamId);
  const current = useRow("cycle", p.value);
  if (team && !team.cyclesEnabled) return null;
  const today = new Date().toISOString().slice(0, 10);
  const now = currentCycle(cycles);
  const upcoming = cycles.filter((c) => c.endsOn > today && !c.completedAt);
  const options: PickerOption<Id | null>[] = [
    { value: null, label: "No cycle", icon: <CircleDashed className="size-4 text-muted-foreground" /> },
    ...upcoming.map((c) => ({ value: c.id, label: cycleName(c), icon: <RefreshCcw className="size-4" />, hint: c.id === now?.id ? "Current" : `${shortDate(c.startsOn)} – ${shortDate(c.endsOn)}` })),
  ];
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder="Move to cycle…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={!p.value}>
          <RefreshCcw className="size-3.5" />
          {current ? cycleName(current) : "Cycle"}
        </Chip>
      )}
    </Picker>
  );
}

export function EstimatePicker({ teamId, ...p }: Controlled<number | null> & { teamId: Id }) {
  const team = useRow("team", teamId);
  const scale = team?.estimateScale ?? "fibonacci";
  if (scale === "none") return null;
  const options: PickerOption<number | null>[] = [{ value: null, label: "No estimate", icon: <Triangle className="size-4 text-muted-foreground" /> }, ...ESTIMATES[scale].map((e) => ({ value: e.value, label: e.label, icon: <Triangle className="size-4" /> }))];
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder="Set estimate…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={p.value == null}>
          <Triangle className="size-3.5" />
          {p.value == null ? "Estimate" : estimateLabel(p.value, scale)}
        </Chip>
      )}
    </Picker>
  );
}

export function DatePicker({ value, onChange, children, label = "Due date", align }: Controlled<string | null> & { label?: string }) {
  const due = dueLabel(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        {children ?? (
          <Chip muted={!value} className={cn(due?.tone === "overdue" && "text-destructive")}>
            <CalendarDays className="size-3.5" />
            {value ? shortDate(value) : label}
          </Chip>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align={align ?? "start"} onClick={(e) => e.stopPropagation()}>
        <Calendar mode="single" selected={value ? parseISO(value) : undefined} onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : null)} autoFocus />
        {value && (
          <button type="button" className="mt-1 w-full rounded-md py-1 text-xs text-muted-foreground hover:bg-accent" onClick={() => onChange(null)}>
            Clear {label.toLowerCase()}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function TeamPicker(p: Controlled<Id> & { onlyMine?: boolean }) {
  const teams = useTeams();
  const current = useRow("team", p.value);
  const options = teams.map((t) => ({ value: t.id, label: t.name, icon: <TeamIcon teamId={t.id} />, hint: t.key }));
  return (
    <Picker options={options} value={p.value} onChange={p.onChange} placeholder="Choose team…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip>
          <TeamIcon teamId={p.value} className="size-4 text-[8px]" />
          {current?.key ?? "Team"}
        </Chip>
      )}
    </Picker>
  );
}

export function TeamsPicker(p: Controlled<Id[]>) {
  const teams = useTeams();
  const all = useTable("team");
  const options = teams.map((t) => ({ value: t.id, label: t.name, icon: <TeamIcon teamId={t.id} />, hint: t.key }));
  return (
    <Picker multiple options={options} value={p.value} onChange={p.onChange} placeholder="Choose teams…" open={p.open} onOpenChange={p.onOpenChange} align={p.align}>
      {p.children ?? (
        <Chip muted={p.value.length === 0}>
          <Users className="size-3.5" />
          {p.value.length ? p.value.map((t) => all[t]?.key).join(", ") : "Teams"}
        </Chip>
      )}
    </Picker>
  );
}

export function LeadPicker(p: Controlled<Id | null> & { label?: string }) {
  return (
    <AssigneePicker {...p} label={p.label ?? "Lead"}>
      {p.children ?? undefined}
    </AssigneePicker>
  );
}

export { UserRound };
